import React from 'react';
import Grid from '@material-ui/core/Grid';
import TextField from '@material-ui/core/TextField';
import AsyncSelectControl from '../../Components/AsyncSelectControl';
import SelectControl from '../../Components/SelectControl';
import ProcessElementDialogHelper from '../../Util/ProcessElementDialogs';

export default function GetContent(location, history, organizationId, projectId, processElement, processElementConnections_Input,
  onGetLocalState, onSetLocalState, onGetLocalProperty, onSetLocalProperty,
  onApiError, onAlert, onGetListValuesAndLabelsPropertyName, onChangeHandlers,
  onSelectionListValueChange, onSelectionListCreateOption, onSetCloseDialogFunc,
  onSetShowFieldPropertiesDialog, onSetManageFieldsDialogCloseFunc) {
    
    const dh = new ProcessElementDialogHelper(location, history, organizationId, projectId, processElement, processElementConnections_Input,
      onGetLocalState, onSetLocalState, onGetLocalProperty, onSetLocalProperty, onApiError, onAlert, 
      onGetListValuesAndLabelsPropertyName, onChangeHandlers, onSelectionListValueChange, onSelectionListCreateOption, onSetCloseDialogFunc,
      onSetShowFieldPropertiesDialog, onSetManageFieldsDialogCloseFunc);

    // Get the listValueId for ItemType
    const itemTypeListValueId = onGetListValuesAndLabelsPropertyName("ItemType", false);
    const selectedItemType = processElement[itemTypeListValueId];
    const isAssetItem = selectedItemType && selectedItemType.value === "AssetItem";

    const itemTypeOptions = [
      {label:"Document",value:"Document"},
      {label:"Asset",value:"AssetItem"},
    ];

    // Default to 7 days (10080 minutes) if no value persisted
    const triggerOffsetMinutes = processElement.Data["TriggerOffsetMinutes"] !== undefined 
      ? processElement.Data["TriggerOffsetMinutes"] 
      : 10080;
    
    // Initialize multiplier and interval based on whether we have persisted values
    const hasPersistedValues = processElement.Data["TriggerOffsetMinutes"] !== undefined;
    const offsetMultiplier = processElement.Data["OffsetMultiplier"] || (hasPersistedValues ? null : 7);
    const offsetInterval = processElement.Data["OffsetInterval"] || (hasPersistedValues ? null : "Days");
    const offsetDirection = processElement.Data["OffsetDirection"] || "After";

    // Generate multiplier options (1 to 100)
    const multiplierOptions = [];
    for (let i = 1; i <= 100; i++) {
      multiplierOptions.push({ value: i, label: i.toString() });
    }

    const intervalOptions = [
      { value: "Hours", label: "Hours" },
      { value: "Days", label: "Days" },
      { value: "Weeks", label: "Weeks" },
      { value: "Months", label: "Months" }
    ];

    const directionOptions = [
      { value: "Before", label: "Before" },
      { value: "After", label: "After" }
    ];

    // Calculate offset minutes when multiplier or interval changes
    const calculateOffsetMinutes = (multiplier, interval, direction) => {
      let minutes = multiplier;
      switch (interval) {
        case "Hours":
          minutes = multiplier * 60;
          break;
        case "Days":
          minutes = multiplier * 60 * 24;
          break;
        case "Weeks":
          minutes = multiplier * 60 * 24 * 7;
          break;
        case "Months":
          minutes = multiplier * 60 * 24 * 30; // Approximate
          break;
      }
      // Apply direction (negative for "Before")
      return direction === "Before" ? -minutes : minutes;
    };

    const handleDirectionChange = (value) => {
      onChangeHandlers.setStringProperty("OffsetDirection", true, value);
      if (offsetMultiplier && offsetInterval) {
        const minutes = calculateOffsetMinutes(offsetMultiplier, offsetInterval, value);
        onChangeHandlers.setNumberProperty("TriggerOffsetMinutes", true, minutes);
      } else {
        // If no multiplier/interval, just flip the sign of the current minutes
        const currentMinutes = Math.abs(triggerOffsetMinutes);
        onChangeHandlers.setNumberProperty("TriggerOffsetMinutes", true, value === "Before" ? -currentMinutes : currentMinutes);
      }
    };

    const handleMultiplierChange = (value) => {
      onChangeHandlers.setNumberProperty("OffsetMultiplier", true, value);
      if (offsetInterval) {
        const minutes = calculateOffsetMinutes(value, offsetInterval, offsetDirection);
        onChangeHandlers.setNumberProperty("TriggerOffsetMinutes", true, minutes);
      }
    };

    const handleIntervalChange = (value) => {
      onChangeHandlers.setStringProperty("OffsetInterval", true, value);
      if (offsetMultiplier) {
        const minutes = calculateOffsetMinutes(offsetMultiplier, value, offsetDirection);
        onChangeHandlers.setNumberProperty("TriggerOffsetMinutes", true, minutes);
      }
    };

    return (
      <Grid container direction="column" spacing={2}
        style={{
          /*Fix for asyncselectcontrols with value*/flexWrap:"noWrap"
        }}>
        <Grid item>
          <AsyncSelectControl label="Item type"
            onGetOptionsFilterPromise={filter => Promise.resolve(itemTypeOptions.filter(lv => lv.label.toLowerCase().startsWith(filter.toLowerCase())))} 
            listValues={selectedItemType}
            onValueChange={onSelectionListValueChange("ItemType", false)}
          />
        </Grid>
        <Grid item>
          <AsyncSelectControl label="Date field" 
            placeholder="Select date field..."
            onGetOptionsFilterPromise={dh.GetDateFieldListValuesPromise} 
            listValues={processElement[onGetListValuesAndLabelsPropertyName("DateFieldID", false)]}
            onValueChange={onSelectionListValueChange("DateFieldID", false)}
          />
        </Grid>
        <Grid item>
          <Grid container spacing={2}>
            <Grid item xs={2}>
              <SelectControl
                label="Offset multiplier"
                options={multiplierOptions}
                value={offsetMultiplier || ''}
                onValueChange={handleMultiplierChange}
                hideEmpty={false}
              />
            </Grid>
            <Grid item xs={2}>
              <SelectControl
                label="Offset interval"
                options={intervalOptions}
                value={offsetInterval || ''}
                onValueChange={handleIntervalChange}
                hideEmpty={false}
              />
            </Grid>
            <Grid item xs={2}>
              <SelectControl
                label="Direction"
                options={directionOptions}
                value={offsetDirection}
                onValueChange={handleDirectionChange}
                hideEmpty
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                variant="outlined"
                label="Offset (minutes)"
                type="number"
                value={triggerOffsetMinutes}
                helperText="Number of minutes before (negative) or after (positive) the date field value to trigger"
                onChange={e => {
                  const minutes = parseInt(e.target.value) || 0;
                  onChangeHandlers.setNumberProperty("TriggerOffsetMinutes", true, minutes);
                  // Clear multiplier/interval when manually editing minutes
                  onChangeHandlers.setNumberProperty("OffsetMultiplier", true, null);
                  onChangeHandlers.setStringProperty("OffsetInterval", true, null);
                  // Update direction based on sign
                  onChangeHandlers.setStringProperty("OffsetDirection", true, minutes < 0 ? "Before" : "After");
                }}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Grid>
          </Grid>
        </Grid>
        {isAssetItem ? (
          <Grid item>
            {dh.GetTriggerContentForAssets()}
          </Grid>
        ) : (
          <Grid item>
            {dh.GetTriggerContentForDocumentFolders()}
          </Grid>
        )}
        <Grid item>
          {dh.GetTriggerContentForFields()}
        </Grid>
        <Grid item>
          {dh.GetTriggerContentForTags()}
        </Grid>
      </Grid>
    );
  }